import React, { useEffect, useState } from "react";
import {
  X,
  Send,
  Clock,
  User,
  AlertTriangle,
  Brain,
  Zap,
  Shield,
  Trash2,
  ChevronDown,
  CreditCard,
  CalendarX,
  Terminal,
  FileText,
  History,
  CheckCircle2,
  MessageCircle,
  ExternalLink,
} from "lucide-react";
import {
  SupportTicket,
  SupportMessage,
  CustomerInfo,
  supportTicketService,
} from "@core/services/SupportTicketService";
import { AIAnalysisResult } from "@core/services/SupportAIService";
import { TicketStatusManager } from "./TicketStatusManager";
import { CustomerPlanBadge } from "./CustomerPlanBadge";
import { DraftMessageCard } from "./DraftMessageCard";
import { useAuth } from "@core/contexts/AuthContext";
import { SupportAIService } from "@core/services/SupportAIService";
import { supabase } from "@core/lib/supabase";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";
import { Paperclip, Download } from "lucide-react";

interface TicketSlidePanelProps {
  ticketId: string | null;
  ticket: SupportTicket | null;
  onClose: () => void;
  onTicketUpdate: (updatedTicket: SupportTicket) => void;
  onTicketDeleted?: (ticketId: string) => void;
}

// ---------- Helpers ----------

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  accent?: "purple" | "blue" | "default";
  badge?: React.ReactNode;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  defaultOpen = false,
  accent = "default",
  badge,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const accentBorder =
    accent === "purple"
      ? "border-purple-500/30"
      : accent === "blue"
        ? "border-blue-500/30"
        : "border-white/10";
  const accentBg =
    accent === "purple"
      ? "bg-gradient-to-br from-purple-900/10 to-blue-900/10"
      : "bg-white/5";
  return (
    <div
      className={`${accentBg} border ${accentBorder} rounded-xl overflow-hidden`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 text-gray-200 font-semibold text-sm">
          {icon}
          {title}
          {badge}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

const CustomerCard: React.FC<{
  customer: CustomerInfo | null | undefined;
  email: string;
}> = ({ customer, email }) => {
  const isPaying = !!(customer?.planType && customer.planType !== "free");
  const isProblematic =
    customer?.subscriptionStatus === "past_due" ||
    customer?.subscriptionStatus === "cancelled";
  const periodEndDate = customer?.periodEnd
    ? new Date(customer.periodEnd).toLocaleDateString("fr-FR")
    : null;

  return (
    <div
      className={`rounded-xl p-4 border ${
        isPaying
          ? "bg-gradient-to-br from-purple-900/20 to-amber-900/10 border-purple-500/40"
          : "bg-white/5 border-white/10"
      }`}
    >
      <div className="flex items-center gap-2 text-gray-300 mb-3 font-semibold text-xs uppercase tracking-wider">
        <User className="w-3.5 h-3.5 text-purple-400" />
        Client
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <CustomerPlanBadge
          planType={customer?.planType}
          subscriptionStatus={customer?.subscriptionStatus}
        />
        {customer?.subscriptionStatus && (
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
              isProblematic
                ? "bg-red-500/10 text-red-300 border-red-500/30"
                : "bg-green-500/10 text-green-300 border-green-500/30"
            }`}
          >
            {customer.subscriptionStatus}
          </span>
        )}
      </div>

      <div className="text-xs text-gray-400 break-all mb-2">{email}</div>

      {customer?.cancelAtPeriodEnd && periodEndDate && (
        <div className="flex items-start gap-2 text-[11px] text-orange-300 bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 mt-2">
          <CalendarX className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>Annulé — prend fin le {periodEndDate}</span>
        </div>
      )}

      {!customer?.cancelAtPeriodEnd && periodEndDate && (
        <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-2">
          <CreditCard className="w-3 h-3" />
          Prochain prélèvement : {periodEndDate}
        </div>
      )}
    </div>
  );
};

// Liste des pièces jointes — génère des signed URLs depuis le bucket
// `support-attachments`, où le webhook Resend inbound stocke les fichiers
// au chemin `{ticketId}/{filename}`.
const AttachmentsCard: React.FC<{
  ticketId: string;
  attachments: Array<{
    filename: string;
    contentType?: string;
    size?: number;
  }>;
}> = ({ ticketId, attachments }) => {
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const att of attachments) {
        try {
          const path = `${ticketId}/${att.filename}`;
          const { data } = await supabase.storage
            .from("support-attachments")
            .createSignedUrl(path, 60 * 60); // 1h
          if (data?.signedUrl) next[att.filename] = data.signedUrl;
        } catch {
          // ignore — on affiche juste le nom sans lien
        }
      }
      if (!cancelled) setUrls(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId, attachments]);

  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-gray-300 mb-3 font-semibold text-xs uppercase tracking-wider">
        <Paperclip className="w-3.5 h-3.5 text-blue-400" />
        Pièces jointes
        <span className="text-gray-500 font-normal">
          ({attachments.length})
        </span>
      </div>
      <ul className="space-y-2">
        {attachments.map((att) => {
          const url = urls[att.filename];
          const isImage = att.contentType?.startsWith("image/");
          return (
            <li
              key={att.filename}
              className="bg-black/20 border border-white/5 rounded-lg p-2"
            >
              {isImage && url && (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={url}
                    alt={att.filename}
                    className="w-full rounded mb-2"
                  />
                </a>
              )}
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="text-gray-200 truncate" title={att.filename}>
                    {att.filename}
                  </div>
                  <div className="text-gray-500 text-[10px]">
                    {att.contentType}
                    {att.size ? ` · ${formatSize(att.size)}` : ""}
                  </div>
                </div>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={att.filename}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-300 border border-blue-500/30 rounded hover:bg-blue-600/30 flex-shrink-0"
                    title="Télécharger"
                  >
                    <Download className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-gray-600 text-[10px]">
                    lien indispo
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ---------- Main ----------

export const TicketSlidePanel: React.FC<TicketSlidePanelProps> = ({
  ticketId,
  ticket,
  onClose,
  onTicketUpdate,
  onTicketDeleted,
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!ticketId) return;

    const loadMessages = async () => {
      try {
        // Client authentifié → RLS support_messages évaluée avec la session
        // admin (vs. l'anon key qui pouvait renvoyer 0 messages).
        const { data, error } = await supabase
          .from("support_messages")
          .select("*")
          .eq("ticket_id", ticketId)
          .order("created_at", { ascending: true });
        if (error) {
          console.error("Failed to load messages:", error);
          return;
        }
        setMessages((data as SupportMessage[]) || []);
      } catch (error) {
        console.error("Failed to load messages:", error);
      }
    };

    loadMessages();
  }, [ticketId]);

  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [urgencyDetection, setUrgencyDetection] = useState<any>(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestion, setShowAISuggestion] = useState(false);

  const calculateSLA = (ticket: SupportTicket) => {
    const slaDays = SupportAIService.getSLADays(ticket.severity as any);
    const createdDate = new Date(ticket.created_at);
    const dueDate = new Date(
      createdDate.getTime() + slaDays * 24 * 60 * 60 * 1000,
    );
    const now = new Date();
    const overdueDays = Math.floor(
      (now.getTime() - dueDate.getTime()) / (24 * 60 * 60 * 1000),
    );

    return {
      isOverdue: overdueDays >= 0,
      overdueDays,
      dueDate,
      slaDays,
      daysRemaining: Math.abs(overdueDays),
    };
  };

  const getPriorityColor = (priority?: number | null) => {
    switch (priority) {
      case 3:
        return "bg-red-900/20 text-red-300 border-red-500/50";
      case 2:
        return "bg-orange-900/20 text-orange-300 border-orange-500/50";
      case 1:
        return "bg-yellow-900/20 text-yellow-300 border-yellow-500/50";
      case 0:
        return "bg-green-900/20 text-green-300 border-green-500/50";
      default:
        return "bg-gray-900/20 text-gray-300 border-gray-500/50";
    }
  };

  const getPriorityLabel = (priority?: number | null) => {
    switch (priority) {
      case 3:
        return "Critique";
      case 2:
        return "Haute";
      case 1:
        return "Normale";
      case 0:
        return "Basse";
      default:
        return "Non définie";
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleTicketUpdate = async (updatedTicket: SupportTicket | null) => {
    if (updatedTicket) {
      onTicketUpdate(updatedTicket);

      if (ticket?.status !== updatedTicket.status) {
        const statusMessages = {
          in_progress: "Ticket pris en charge avec succès !",
          resolved: "Ticket marqué comme résolu !",
          closed: "Ticket fermé !",
        };

        const message =
          statusMessages[updatedTicket.status as keyof typeof statusMessages];
        if (message) {
          const notification = document.createElement("div");
          notification.className =
            "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[10000] animate-pulse";
          notification.textContent = message;
          document.body.appendChild(notification);

          setTimeout(() => {
            notification.remove();
          }, 3000);
        }
      }
    }
  };

  const handleReply = async () => {
    if (!reply.trim() || !ticket) return;

    setSending(true);
    try {
      const newMessage = await supportTicketService.addMessage(
        ticket.id,
        reply,
        "agent",
        user?.email,
      );

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Session admin requise pour envoyer la réponse");
      }

      try {
        await invokeSupabaseFunction(
          "send-support-reply-email",
          {
            ticketId: ticket.id,
            ticketSubject: ticket.subject,
            userEmail: ticket.email,
            agentMessage: reply,
            ticketUrl: `https://doodates.com/admin/support?ticket=${ticket.id}`,
          },
          { timeout: 15000 },
        );
      } catch (e) {
        console.warn("Email non envoyé (message enregistré):", e);
      }

      setMessages([...messages, newMessage]);
      setReply("");

      const notification = document.createElement("div");
      notification.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[10000] animate-pulse";
      notification.textContent = "Réponse envoyée avec succès !";
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    } catch (error) {
      console.error("Failed to send reply:", error);
      alert(
        "Erreur lors de l'envoi de la réponse: " +
          (error instanceof Error ? error.message : String(error)),
      );
    } finally {
      setSending(false);
    }
  };

  const handleSuggestResponse = async () => {
    if (!ticket || loadingAI) return;

    setLoadingAI(true);
    try {
      const aiService = SupportAIService.getInstance();
      const response = await aiService.suggestAgentResponse(
        {
          subject: ticket.subject,
          message: ticket.metadata?.message || ticket.subject,
          severity: ticket.severity,
        },
        {
          userProfile: {
            name: ticket.email.split("@")[0],
            email: ticket.email,
          },
          agentPreferences: {
            tone: "professional",
            language: "fr",
          },
        },
      );

      if (response) {
        setAiSuggestion(response.suggestedResponse);
        setShowAISuggestion(true);
      }
    } catch (error) {
      console.error("Error getting AI suggestion:", error);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleDetectUrgency = async () => {
    if (!ticket || loadingAI) return;

    setLoadingAI(true);
    try {
      const aiService = SupportAIService.getInstance();
      const urgency = await aiService.detectUrgency({
        subject: ticket.subject,
        message: ticket.metadata?.message || ticket.subject,
        severity: ticket.severity,
        createdAt: ticket.created_at,
      });

      if (urgency) {
        setUrgencyDetection(urgency);
      }
    } catch (error) {
      console.error("Error detecting urgency:", error);
    } finally {
      setLoadingAI(false);
    }
  };

  const useAISuggestion = () => {
    if (aiSuggestion) {
      setReply(aiSuggestion);
      setShowAISuggestion(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticket) return;

    if (
      !confirm(
        "Êtes-vous sûr de vouloir supprimer ce ticket ? Cette action est irréversible.",
      )
    ) {
      return;
    }

    try {
      await supportTicketService.deleteTicket(ticket.id);
      onTicketDeleted?.(ticket.id);
      onClose();
    } catch (error) {
      console.error("Failed to delete ticket:", error);
      alert(
        "Erreur lors de la suppression du ticket: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  if (!ticketId) return null;

  const aiAnalysis = ticket?.metadata?.ai_analysis as
    | AIAnalysisResult
    | undefined;
  const slaInfo = ticket ? calculateSLA(ticket) : null;
  const isPaying =
    ticket?.customer?.planType && ticket.customer.planType !== "free";

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/70 z-[9999]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] sm:max-w-7xl h-[95vh] bg-gray-900 border border-gray-700 z-[9999] rounded-lg shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 transition-colors flex-shrink-0"
              title="Fermer (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
            {ticket && (
              <>
                <span
                  className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${
                    ticket.status === "open"
                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                      : ticket.status === "in_progress"
                        ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                        : ticket.status === "resolved"
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                  }`}
                >
                  {ticket.status}
                </span>
                <CustomerPlanBadge
                  planType={ticket.customer?.planType}
                  subscriptionStatus={ticket.customer?.subscriptionStatus}
                />
              </>
            )}
            <h2 className="text-sm font-medium text-gray-400 truncate min-w-0">
              #{ticketId.slice(-8)}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => {
                window.open(`/admin/support?ticket=${ticket?.id}`, "_blank");
              }}
              className="p-2 rounded-lg hover:bg-blue-600/20 text-blue-400 transition-colors"
              title="Ouvrir dans un navigateur"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={handleDeleteTicket}
              className="p-2 rounded-lg hover:bg-red-600/20 text-red-400 transition-colors"
              title="Supprimer le ticket"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body — 2 columns */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {!ticket ? (
            <div className="flex-1 p-4 text-center text-gray-400">
              Ticket non trouvé
            </div>
          ) : (
            <>
              {/* ===== LEFT: conversation ===== */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5 min-w-0">
                {/* Subject */}
                <div>
                  <h1 className="text-xl font-bold text-white leading-tight mb-2">
                    {ticket.subject}
                  </h1>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-gray-200">{ticket.email}</span>
                      {/* Le ticket ne porte pas l'identifiant du compte (jointure
                          par email) : on passe par la recherche de la liste des
                          utilisateurs, d'où la timeline est à un clic. */}
                      <a
                        href={`/admin/users?search=${encodeURIComponent(ticket.email)}`}
                        className="text-blue-400 hover:underline"
                        title="Voir la timeline support de cet utilisateur"
                      >
                        timeline
                      </a>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(ticket.created_at).toLocaleString("fr-FR")}
                    </div>
                    {ticket.severity && (
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {ticket.severity}
                      </div>
                    )}
                  </div>
                </div>

                {/* Corps brut de l'email quand le webhook a dû retomber
                    sur le sujet (metadata.body_fallback_used), ou quand
                    le premier message stocké n'est visiblement que le
                    sujet (tickets d'avant le fix d'extraction). */}
                {(() => {
                  const originalBody = ticket.metadata?.original_body as
                    | string
                    | undefined;
                  const firstUser = messages.find(
                    (m) => m.sender_type === "user",
                  );
                  const messageIsJustSubject = !!(
                    firstUser &&
                    firstUser.content.trim() === ticket.subject.trim()
                  );
                  const showFallback =
                    (ticket.metadata?.body_fallback_used ||
                      messageIsJustSubject) &&
                    originalBody &&
                    originalBody !== firstUser?.content;
                  if (!showFallback) return null;
                  return (
                    <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-xl p-3">
                      <div className="text-[10px] text-yellow-400 uppercase tracking-wider mb-2 font-semibold">
                        ⚠ Corps original (le message stocké semble n'être que le
                        sujet)
                      </div>
                      <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
                        {originalBody}
                      </p>
                    </div>
                  );
                })()}

                {/* Conversation (la 1re ligne sender_type='user' contient
                    le body de l'email d'origine pour les tickets inbound) */}
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-purple-400" />
                    Conversation
                    <span className="text-xs font-normal text-gray-500">
                      ({messages.length})
                    </span>
                  </h2>

                  {messages.filter((m) => m.status !== "draft").length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                      Aucun message pour l'instant.
                    </p>
                  ) : (
                    messages
                      .filter((m) => m.status !== "draft")
                      .map((message) => (
                        <div
                          key={message.id}
                          className={`flex flex-col ${
                            message.sender_type === "agent" ||
                            message.sender_type === "ia"
                              ? "items-end"
                              : "items-start"
                          }`}
                        >
                          <div
                            className={`max-w-[85%] rounded-xl p-3 ${
                              message.sender_type === "agent" ||
                              message.sender_type === "ia"
                                ? "bg-purple-600/20 border border-purple-500/30 rounded-tr-none"
                                : "bg-white/5 border border-white/10 rounded-tl-none"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1.5 gap-4 text-xs">
                              <span className="font-semibold text-gray-300">
                                {message.sender_type === "agent"
                                  ? "Agent Support"
                                  : message.sender_type === "ia"
                                    ? "Agent Support (IA)"
                                    : ticket.email}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {new Date(
                                  message.created_at,
                                ).toLocaleTimeString("fr-FR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">
                              {message.content}
                            </p>
                          </div>
                        </div>
                      ))
                  )}
                </div>

                {/* Brouillons IA (Claude triage) — en attente de validation admin */}
                {(() => {
                  const drafts = messages.filter((m) => m.status === "draft");
                  if (drafts.length === 0) return null;
                  return (
                    <div
                      className="space-y-2"
                      data-testid="draft-messages-section"
                    >
                      <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-400" />
                        Brouillons IA
                        <span className="text-xs font-normal text-gray-500">
                          ({drafts.length})
                        </span>
                      </h2>
                      {drafts.map((draft) => (
                        <DraftMessageCard
                          key={draft.id}
                          draft={draft}
                          ticket={ticket}
                          onSent={(updated) =>
                            setMessages((prev) =>
                              prev.map((m) =>
                                m.id === updated.id ? updated : m,
                              ),
                            )
                          }
                          onEdited={(updated) =>
                            setMessages((prev) =>
                              prev.map((m) =>
                                m.id === updated.id ? updated : m,
                              ),
                            )
                          }
                          onRejected={(id) =>
                            setMessages((prev) =>
                              prev.filter((m) => m.id !== id),
                            )
                          }
                        />
                      ))}
                    </div>
                  );
                })()}

                {/* AI alerts (inline in conversation column) */}
                {showAISuggestion && aiSuggestion && (
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">
                          Suggestion IA
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={useAISuggestion}
                          className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                        >
                          Utiliser
                        </button>
                        <button
                          onClick={() => setShowAISuggestion(false)}
                          className="px-2 py-0.5 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                        >
                          Ignorer
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">{aiSuggestion}</p>
                  </div>
                )}

                {urgencyDetection && urgencyDetection.isUrgent && (
                  <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-orange-400" />
                      <span className="text-xs font-semibold text-orange-300 uppercase tracking-wider">
                        Urgence :{" "}
                        {urgencyDetection.urgencyLevel?.toUpperCase?.()}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {urgencyDetection.reasons?.map(
                        (reason: string, index: number) => (
                          <p key={index} className="text-xs text-gray-300">
                            • {reason}
                          </p>
                        ),
                      )}
                    </div>
                    {urgencyDetection.escalationNeeded && (
                      <p className="text-xs text-orange-400 mt-2">
                        ⚠️ Escalade recommandée :{" "}
                        {urgencyDetection.recommendedAction}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ===== RIGHT: sidebar ===== */}
              <aside className="lg:w-[340px] lg:border-l border-t lg:border-t-0 border-gray-700 overflow-y-auto p-4 space-y-3 bg-gray-900/60 flex-shrink-0">
                {/* Customer */}
                <CustomerCard customer={ticket.customer} email={ticket.email} />

                {/* Pièces jointes (bucket support-attachments) */}
                {ticket.metadata?.attachments &&
                  Array.isArray(ticket.metadata.attachments) &&
                  ticket.metadata.attachments.length > 0 && (
                    <AttachmentsCard
                      ticketId={ticket.id}
                      attachments={ticket.metadata.attachments}
                    />
                  )}

                {/* Status + SLA */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-300 mb-3 font-semibold text-xs uppercase tracking-wider">
                    <History className="w-3.5 h-3.5 text-purple-400" />
                    Gestion du ticket
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    <span
                      className={`px-2 py-1 rounded text-[10px] font-bold border ${getPriorityColor(ticket.priority)}`}
                    >
                      P{ticket.priority ?? "?"} —{" "}
                      {getPriorityLabel(ticket.priority)}
                    </span>
                    {slaInfo && (
                      <span
                        className={`px-2 py-1 rounded text-[10px] font-bold border ${
                          slaInfo.isOverdue
                            ? "bg-red-500/10 text-red-300 border-red-500/30"
                            : "bg-green-500/10 text-green-300 border-green-500/30"
                        }`}
                      >
                        {slaInfo.isOverdue
                          ? `SLA ${slaInfo.overdueDays}j retard`
                          : `SLA ${slaInfo.daysRemaining}j restants`}
                      </span>
                    )}
                  </div>

                  <TicketStatusManager
                    ticket={ticket}
                    onStatusChange={handleTicketUpdate}
                    currentUserId={user?.id}
                  />
                </div>

                {/* AI actions */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-300 mb-3 font-semibold text-xs uppercase tracking-wider">
                    <Brain className="w-3.5 h-3.5 text-purple-400" />
                    Actions IA
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleSuggestResponse}
                      disabled={loadingAI}
                      className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                    >
                      <Brain className="w-3.5 h-3.5" />
                      {loadingAI ? "Analyse..." : "Suggérer une réponse"}
                    </button>
                    <button
                      onClick={handleDetectUrgency}
                      disabled={loadingAI}
                      className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                    >
                      <Zap className="w-3.5 h-3.5" />
                      {loadingAI ? "Analyse..." : "Détecter l'urgence"}
                    </button>
                  </div>
                </div>

                {/* Pre-computed AI analysis */}
                {aiAnalysis && (
                  <CollapsibleSection
                    title="Analyse IA pré-calculée"
                    icon={<Brain className="w-3.5 h-3.5 text-purple-400" />}
                    defaultOpen={!!isPaying}
                    accent="purple"
                  >
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-black/20 rounded-lg p-2 border border-white/5">
                          <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                            Classification
                          </div>
                          <div className="text-white text-xs font-medium">
                            {aiAnalysis.classification}
                          </div>
                        </div>
                        <div className="bg-black/20 rounded-lg p-2 border border-white/5">
                          <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                            Sévérité
                          </div>
                          <div
                            className={`text-xs font-bold ${
                              aiAnalysis.severity === "Critical"
                                ? "text-red-400"
                                : aiAnalysis.severity === "High"
                                  ? "text-orange-400"
                                  : "text-blue-400"
                            }`}
                          >
                            {aiAnalysis.severity}
                          </div>
                        </div>
                      </div>

                      {aiAnalysis.summary && (
                        <div className="bg-black/20 rounded-lg p-2 border border-white/5">
                          <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <FileText className="w-2.5 h-2.5" /> Résumé
                          </div>
                          <p className="text-[11px] text-gray-300 italic">
                            "{aiAnalysis.summary}"
                          </p>
                        </div>
                      )}

                      {aiAnalysis.diagnostic && (
                        <div className="bg-black/20 rounded-lg p-2 border border-white/5">
                          <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <Terminal className="w-2.5 h-2.5" /> Diagnostic
                          </div>
                          <p className="text-[11px] text-gray-300 leading-relaxed font-mono">
                            {aiAnalysis.diagnostic}
                          </p>
                        </div>
                      )}

                      {aiAnalysis.suggestedAction && (
                        <div className="flex items-start gap-2 text-[11px] text-green-300 bg-green-500/10 border border-green-500/20 rounded-lg p-2">
                          <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {aiAnalysis.suggestedAction}
                        </div>
                      )}

                      {aiAnalysis.suggestedResponse && (
                        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-2">
                          <div className="text-[9px] text-purple-400 uppercase tracking-wider mb-1">
                            Réponse suggérée
                          </div>
                          <p className="text-[11px] text-purple-200 leading-relaxed whitespace-pre-wrap">
                            {aiAnalysis.suggestedResponse}
                          </p>
                          <button
                            onClick={() =>
                              setReply(aiAnalysis.suggestedResponse || "")
                            }
                            className="mt-2 text-[10px] text-purple-300 hover:text-purple-200 underline"
                          >
                            Utiliser cette réponse
                          </button>
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Technical metadata */}
                {ticket.metadata &&
                  (ticket.metadata.screenshot ||
                    ticket.metadata.url ||
                    ticket.metadata.userAgent ||
                    (ticket.metadata.errors &&
                      ticket.metadata.errors.length > 0) ||
                    (ticket.metadata.logs &&
                      ticket.metadata.logs.length > 0)) && (
                    <CollapsibleSection
                      title="Métadonnées techniques"
                      icon={<Terminal className="w-3.5 h-3.5 text-blue-400" />}
                      defaultOpen={false}
                      accent="blue"
                    >
                      <div className="space-y-3">
                        {ticket.metadata.screenshot && (
                          <div>
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                              Capture d'écran
                            </div>
                            <div className="bg-black/20 rounded-lg p-1 border border-white/5">
                              <img
                                src={ticket.metadata.screenshot}
                                alt="Screenshot"
                                className="w-full rounded"
                              />
                            </div>
                          </div>
                        )}

                        {ticket.metadata.url && (
                          <div>
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                              URL
                            </div>
                            <div className="text-[11px] text-gray-300 font-mono break-all">
                              {ticket.metadata.url}
                            </div>
                          </div>
                        )}

                        {ticket.metadata.userAgent && (
                          <div>
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                              Navigateur / OS
                            </div>
                            <div className="text-[11px] text-gray-300 break-words">
                              {ticket.metadata.userAgent}
                            </div>
                          </div>
                        )}

                        {ticket.metadata.screenSize && (
                          <div>
                            <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                              Taille écran
                            </div>
                            <div className="text-[11px] text-gray-300 font-mono">
                              {ticket.metadata.screenSize}
                            </div>
                          </div>
                        )}

                        {ticket.metadata.errors &&
                          ticket.metadata.errors.length > 0 && (
                            <div>
                              <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                                Erreurs ({ticket.metadata.errors.length})
                              </div>
                              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 max-h-32 overflow-y-auto">
                                <pre className="text-[9px] text-red-300 font-mono whitespace-pre-wrap">
                                  {JSON.stringify(
                                    ticket.metadata.errors,
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            </div>
                          )}

                        {ticket.metadata.logs &&
                          ticket.metadata.logs.length > 0 && (
                            <div>
                              <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">
                                Logs ({ticket.metadata.logs.length})
                              </div>
                              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 max-h-32 overflow-y-auto">
                                <pre className="text-[9px] text-blue-300 font-mono whitespace-pre-wrap">
                                  {JSON.stringify(
                                    ticket.metadata.logs,
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            </div>
                          )}
                      </div>
                    </CollapsibleSection>
                  )}
              </aside>
            </>
          )}
        </div>

        {/* Reply input (footer, full width) */}
        {ticket && (
          <div className="border-t border-gray-700 p-3 flex-shrink-0 bg-gray-900">
            <div className="flex gap-2">
              <input
                type="text"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Écrire une réponse..."
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                onKeyPress={(e) => e.key === "Enter" && handleReply()}
                disabled={sending}
              />
              <button
                onClick={handleReply}
                disabled={!reply.trim() || sending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {sending ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const TicketSlidePanelMemo = React.memo(TicketSlidePanel);
